import {
  LocationServiceValueExpenses,
  LocationServiceValueIncome,
} from '@vgrubs/components';
import { DateTime } from 'luxon';

export type MoneyLineSpec<K extends string> = {
  key: K;
  label: string;
  /** Shown behind the info icon: how the backend arrives at the number. */
  info?: string;
};

/** Our income from the location, in the Finances dashboard's order. */
export const INCOME_LINES: MoneyLineSpec<
  Exclude<keyof LocationServiceValueIncome, 'total'>
>[] = [
  { key: 'vGrubs', label: 'vGrubs', info: 'Virtual brands.' },
  { key: 'vOrders', label: 'vOrders', info: 'Webshop orders.' },
  {
    key: 'vDrive',
    label: 'vDrive',
    info: 'After the real delivery cost and tips.',
  },
  {
    key: 'saas',
    label: 'SaaS & vTablet',
    info: 'SaaS payments and the vTablet fee, minus the GoCardless fee.',
  },
  {
    key: 'withdraw',
    label: 'vPay withdraw fee',
    info: 'Minus the bank fee.',
  },
  { key: 'vDriveNegativeFee', label: 'vDrive negative balance fee' },
  { key: 'serviceFee', label: 'Kanekt365 service fee' },
];

/** What the location costs us. */
export const EXPENSE_LINES: MoneyLineSpec<
  Exclude<keyof LocationServiceValueExpenses, 'total'>
>[] = [
  { key: 'agentCommissions', label: 'Agent commissions' },
  {
    key: 'vDriveCost',
    label: 'vDrive cost',
    info: 'What we pay for vDrive on the virtual brands.',
  },
  { key: 'foodHub', label: 'FoodHub', info: '$0.10 per completed order.' },
  { key: 'kitchenHub', label: 'KitchenHub', info: '$20 a month.' },
  {
    key: 'esper',
    label: 'Esper',
    info: '$2.60 a month for a tablet location.',
  },
];

/**
 * The line that carries the most, for the card's "mostly ..." hint. Null when
 * every line is 0, so the card says nothing rather than "mostly vGrubs".
 */
export const largestLine = <K extends string>(
  lines: MoneyLineSpec<K>[],
  values: Record<K, number>
) =>
  lines.reduce<MoneyLineSpec<K> | null>(
    (best, line) =>
      values[line.key] > (best ? values[best.key] : 0) ? line : best,
    null
  );

/** "1 order", "7 orders"; `plural` for the nouns that do not just take an s. */
export const countOf = (count: number, noun: string, plural = `${noun}s`) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? noun : plural}`;

/**
 * "Oct 1 - Oct 27, 2026", or with both years when they differ; "All time"
 * when the backend answered without a period.
 */
export const periodLabel = (from: string | null, to: string | null) => {
  if (!from || !to) return 'All time';
  const start = DateTime.fromISO(from);
  const end = DateTime.fromISO(to);
  if (!start.isValid || !end.isValid) return '';
  const sameYear = start.year === end.year;
  return `${start.toFormat(
    sameYear ? 'LLL d' : 'LLL d, yyyy'
  )} - ${end.toFormat('LLL d, yyyy')}`;
};
